import React, { lazy, Suspense, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { dbgV, dbgI, dbgW, rkLog } from './utils/debug';
import { isAuthenticated, getAuth, saveAuth, clearAuth, hasPinSet, hasBiometricRegistered, isOnAuthCallback, tokenExpiresSoon, refreshSession, getRefreshToken, attemptBootRefresh } from './utils/auth';
import { getActiveTenantSlug, setActiveTenantSlug, isSubdomainLocked } from './utils/tenant';
import { stashPostLoginRedirect } from './utils/postLoginRedirect';
import { useForcedMobileLayout } from './utils/useForcedMobileLayout';
import { apiFetch } from './api/client';
// switchTenant dispatches a `tenantChanged` event; App re-keys <Routes> on it
// so every tenant-scoped page remounts and its data effects re-fire.
import Navbar from './components/Navbar';
import { Wordmark } from './components/Wordmark';
import LockScreen from './components/LockScreen';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import EnvBadge from './components/EnvBadge';
import DesktopSiteBanner from './components/DesktopSiteBanner';
import NotFoundPage from './pages/NotFoundPage';
import DemoBanner from './components/DemoBanner';
import InstallBanner from './components/InstallBanner';
import ModuleGate from './components/ModuleGate';
import PasskeyBanner from './components/PasskeyBanner';
import { ModuleProvider } from './contexts/ModuleProvider';
import AppFooter from './components/AppFooter';
import { isDemoTenant, isDemoRegistrationSite } from './utils/tenant';
import DeviceBindingBanner from './components/tracking/DeviceBindingBanner';
import './styles/globals.css';
import ErrorBoundary from './components/ErrorBoundary';

// Route-level code splitting (issue 1190). Every page except the auth entry
// points (Login, AuthCallback) and the 404 is loaded on demand, so the main
// entry chunk stays well under vite-plugin-pwa's 2 MiB precache limit. The
// lazy chunks are still precached by the service worker (globPatterns covers
// **/*.js), so offline navigation keeps working after first install.
const Home = lazy(() => import('./pages/Home'));
const CallHistory = lazy(() => import('./pages/CallHistory'));
const CallDetail = lazy(() => import('./pages/CallDetail'));
const Timecard = lazy(() => import('./pages/Timecard'));
const Protocols = lazy(() => import('./pages/Protocols'));
const PoolProtocols = lazy(() => import('./pages/PoolProtocols'));
const ProtocolIndex = lazy(() => import('./pages/ProtocolIndex'));
const DriveProtocolSyncAdmin = lazy(() => import('./pages/DriveProtocolSyncAdmin'));
const Policies = lazy(() => import('./pages/Policies'));
const Diversion = lazy(() => import('./pages/Diversion'));
const TenantAdmin = lazy(() => import('./pages/TenantAdmin'));
const Patients = lazy(() => import('./pages/Patients'));
const Training = lazy(() => import('./pages/Training'));
const CommsTrainer = lazy(() => import('./pages/CommsTrainer'));
const Members = lazy(() => import('./pages/Members'));
const Messages = lazy(() => import('./pages/Messages'));
const MessagingAdmin = lazy(() => import('./pages/MessagingAdmin'));
const ScopeOfPractice = lazy(() => import('./pages/ScopeOfPractice'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminInbox = lazy(() => import('./pages/AdminInbox'));
const AdminModules = lazy(() => import('./pages/AdminModules'));
const AdminAgencies = lazy(() => import('./pages/AdminAgencies'));
const ToneMonitorAdmin = lazy(() => import('./pages/ToneMonitorAdmin'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const Schedule = lazy(() => import('./pages/Schedule'));
const ScheduleGrid = lazy(() => import('./pages/ScheduleGrid'));
const MySchedule = lazy(() => import('./pages/MySchedule'));
const AvailabilityEditor = lazy(() => import('./pages/AvailabilityEditor'));
const Timesheets = lazy(() => import('./pages/Timesheets'));
const SchedulingSettings = lazy(() => import('./pages/SchedulingSettings'));
const ScheduleEvents = lazy(() => import('./pages/ScheduleEvents'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const DemoRegister = lazy(() => import('./pages/DemoRegister'));
const DemoVerify = lazy(() => import('./pages/DemoVerify'));
const DemoSetPassword = lazy(() => import('./pages/DemoSetPassword'));
const LocationAliasDirectory = lazy(() => import('./pages/LocationAliasDirectory'));
const NerisAdmin = lazy(() => import('./pages/NerisAdmin'));
const NerisReconciliation = lazy(() => import('./pages/NerisReconciliation'));
const MabasAdmin = lazy(() => import('./pages/MabasAdmin'));
const MabasDepartments = lazy(() => import('./pages/MabasDepartments'));
const MabasCards = lazy(() => import('./pages/MabasCards'));
const MabasCardBuilder = lazy(() => import('./pages/MabasCardBuilder'));
const MabasDiv110 = lazy(() => import('./pages/MabasDiv110'));
const MabasMap = lazy(() => import('./pages/MabasMap'));
const DispatchCenters = lazy(() => import('./pages/DispatchCenters'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const AedUnits = lazy(() => import('./pages/AedUnits'));
const MapMarkers = lazy(() => import('./pages/MapMarkers'));
const AedInspections = lazy(() => import('./pages/AedInspections'));
const AedDashboard = lazy(() => import('./pages/AedDashboard'));
const AedProgramAdmin = lazy(() => import('./pages/AedProgramAdmin'));
const InventoryLocations = lazy(() => import('./pages/InventoryLocations'));
const InventoryItems = lazy(() => import('./pages/InventoryItems'));
const ReorderAlerts = lazy(() => import('./pages/ReorderAlerts'));
const MedCheckDashboard = lazy(() => import('./pages/MedCheckDashboard'));
const MedCheckInspections = lazy(() => import('./pages/MedCheckInspections'));
const InspectionSchedules = lazy(() => import('./pages/InspectionSchedules'));
const DisasterReport = lazy(() => import('./pages/DisasterReport'));
const DisasterAdmin = lazy(() => import('./pages/DisasterAdmin'));
const DisasterList = lazy(() => import('./pages/DisasterList'));
const DisasterResponder = lazy(() => import('./pages/DisasterResponder'));
const DisasterCommand = lazy(() => import('./pages/DisasterCommand'));
const DisasterGuestJoin = lazy(() => import('./pages/DisasterGuestJoin'));
const DisasterCommunityRegister = lazy(() => import('./pages/DisasterCommunityRegister'));
const DisasterCommunityReport = lazy(() => import('./pages/DisasterCommunityReport'));
const DisasterGuestSession = lazy(() => import('./pages/DisasterGuestSession'));
const DemoLanding = lazy(() => import('./pages/DemoLanding'));
const DemoAdmin = lazy(() => import('./pages/DemoAdmin'));
const StationDashboard = lazy(() => import('./pages/StationDashboard'));
const FireDangerDetail = lazy(() => import('./pages/FireDangerDetail'));
const EquipmentRecovery = lazy(() => import('./pages/EquipmentRecovery'));
const PlatformAuditLog = lazy(() => import('./pages/PlatformAuditLog'));
const VehicleMap = lazy(() => import('./pages/VehicleMap'));
const LocationCheckpoints = lazy(() => import('./pages/LocationCheckpoints'));
const LocationDisclosure = lazy(() => import('./pages/LocationDisclosure'));
const MedCheckRoute = lazy(() => import('./pages/MedCheckRoute'));
const RigInventory = lazy(() => import('./pages/RigInventory'));
const InventorySettings = lazy(() => import('./pages/InventorySettings'));
const Dhs110Export = lazy(() => import('./pages/Dhs110Export'));

function RouteLoading() {
  return <div style={{ color: '#888', fontSize: 14, padding: 16 }}>Loading…</div>;
}

function UrlChangeLogger() {
  const location = useLocation();
  useEffect(() => {
    const full = location.pathname + location.search + location.hash;
    rkLog('url:', full);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

// Redirect the legacy standalone Event Log route (/log/:callId?) to the merged
// Events tab on CallDetail. Preserves existing bookmarks/deep-links. Falls back
// to the dashboard when no callId is present.
function EventLogRedirect() {
  const { callId } = useParams<{ callId: string }>();
  return <Navigate to={callId ? `/calls/${callId}/events` : '/'} replace />;
}

// ---- Boot auth gate (issue #1508) ------------------------------------------
// Historically Protected/PublicHome decided authorization from
// isAuthenticated() (access-token PRESENCE/validity) alone, on every render,
// with no refresh attempt first. Since the access token TTL is 5 minutes,
// that meant almost any cold reopen (phone idle overnight, PWA backgrounded
// and killed by the OS) bounced straight to /login and forced a full
// OIDC/password re-login — discarding a perfectly good 12-hour refresh token
// sitting unused in localStorage. This mirrors the fix already shipped for
// the dispatcher sub-app (PR #637): attempt a silent refresh BEFORE any gate
// is allowed to decide "unauthenticated".
//
// AuthBootPhase is deliberately narrower than a persistent "authorized" flag:
// once the one-time boot check resolves (either way), every gate goes right
// back to live isAuthenticated() on every render, exactly as before. This
// keeps ordinary post-boot transitions (a fresh login via Login/AuthCallback,
// a session-ending logout) working unchanged — only the initial cold-load
// window is deferred.
type AuthBootPhase = 'checking' | 'resolved';

// Default 'resolved' so anything rendered without a Provider (e.g. isolated
// component tests importing Protected/PublicHome directly, like
// ProtectedSuspense.test.tsx) falls back to today's synchronous behaviour.
const AuthBootContext = createContext<AuthBootPhase>('resolved');

function useAuthBootPhase(): AuthBootPhase {
  return useContext(AuthBootContext);
}

/** Wraps the routed app once. See AuthBootPhase above for why this is a
 * one-shot gate rather than a persistent auth-state provider. */
export function AuthBootGate({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<AuthBootPhase>(() => (
    // Nothing to wait for: either already authenticated (warm tab — the
    // common case), no refresh token to try (genuinely logged out — same as
    // today, no network call, no interim "checking" frame), or we're on
    // /auth/callback, where any localStorage auth state is stale leftover
    // from a previous session (same reasoning as the mount-time profile-fetch
    // guard below) — firing a boot refresh here would race AuthCallback's own
    // code exchange over the same tokens.
    isOnAuthCallback() || isAuthenticated() || !getRefreshToken() ? 'resolved' : 'checking'
  ));
  useEffect(() => {
    if (phase !== 'checking' || isOnAuthCallback()) return;
    let cancelled = false;
    dbgI('AuthBootGate', 'expired access token + refresh token present — attempting boot refresh before any redirect');
    attemptBootRefresh().then(ok => {
      if (cancelled) return;
      dbgI('AuthBootGate', 'boot refresh resolved', { success: ok });
      setPhase('resolved');
    });
    return () => { cancelled = true; };
  }, [phase]);
  return <AuthBootContext.Provider value={phase}>{children}</AuthBootContext.Provider>;
}

/** Neutral placeholder shown only during the boot-refresh window above —
 * matches PublicHome's existing "waiting for profile fetch" screen so a cold
 * load never flashes the login page while a silent refresh is in flight. */
function BootCheckingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg, #0d1b2a)',
    }}>
      <Wordmark variant="split" size={30} />
    </div>
  );
}

export function Protected({ children }: { children: React.ReactNode }) {
  const bootPhase = useAuthBootPhase();
  if (bootPhase === 'checking') return <BootCheckingScreen />;
  const authed = isAuthenticated();
  dbgV('Protected', 'route guard evaluated', { authenticated: authed, path: window.location.pathname });
  if (!authed) {
    // Remember where the user was headed so AuthCallback can return them there
    // after login instead of always dropping them on the dashboard.
    const intended = window.location.pathname + window.location.search;
    stashPostLoginRedirect(intended);
    dbgI('Protected', 'not authenticated — redirecting to /login', { intended });
    return <Navigate to="/login" replace />;
  }
  const isDemo = isDemoTenant();
  return (
    <>
      {isDemo && <DemoBanner />}
      <div style={isDemo ? { paddingTop: 'var(--demo-banner-h, 40px)' } : undefined}>
        <Navbar />
        {/* Inner Suspense (issue 1190): a lazy page's cold chunk shows the
            fallback in the content area only, so the Navbar and footer stay
            mounted. The outer Suspense around <Routes> covers the
            non-Protected lazy routes. */}
        <Suspense fallback={<RouteLoading />}>{children}</Suspense>
        <AppFooter />
      </div>
    </>
  );
}

/** Wraps pages that require a real tenant context (not _system). */
function TenantRequired({ children }: { children: React.ReactNode }) {
  if (getActiveTenantSlug() === '_system') {
    return (
      <div style={{ padding: 'calc(var(--nav-height) + 40px) 20px', maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>&#x1F3E2;</div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>System Admin Context</h2>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
          This page shows department-specific data. Select a department from the context
          picker above, or visit the Admin Dashboard for system-wide stats.
        </p>
        <Link to="/admin" style={{ fontSize: 14, color: 'var(--accent)' }}>Go to Admin Dashboard</Link>
      </div>
    );
  }
  return <>{children}</>;
}

const LOCK_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

// Root route: show the app if authenticated, otherwise redirect to marketing page or login.
function PublicHome() {
  const bootPhase = useAuthBootPhase();
  // While the boot-refresh window (issue #1508) is open, defer to live
  // isAuthenticated() being provisionally false so downstream effects below
  // don't act on a stale "unauthenticated" verdict; the render itself is
  // short-circuited to BootCheckingScreen further down, before either
  // redirect branch is reached.
  const authenticated = bootPhase === 'resolved' && isAuthenticated();
  const subdomain = isSubdomainLocked();
  // On root domains, tenant slug comes from localStorage (seeded at login or by profile fetch).
  // Track it as state so we re-render when the async profile fetch seeds it.
  const [tenantSlug, setTenantSlug] = useState<string | null>(() => getActiveTenantSlug());
  // Timeout guard: if slug still not resolved after 8s, show recovery UI instead of blank screen.
  const [waitTimedOut, setWaitTimedOut] = useState(false);

  useEffect(() => {
    if (!authenticated || subdomain) return;
    // Re-sync on mount: cover writes between useState initializer and listener attach (race window).
    const current = getActiveTenantSlug();
    if (current) setTenantSlug(current);
    // Re-read slug when tenantChanged fires (profile fetch completes and seeds it).
    function onTenantChanged() { setTenantSlug(getActiveTenantSlug()); }
    window.addEventListener('tenantChanged', onTenantChanged);
    return () => window.removeEventListener('tenantChanged', onTenantChanged);
  }, [authenticated, subdomain]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authenticated || subdomain || tenantSlug) return;
    const t = setTimeout(() => setWaitTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [authenticated, subdomain, tenantSlug]);

  dbgV('PublicHome', 'rendering', { authenticated, isSubdomainLocked: subdomain, tenantSlug, path: window.location.pathname });

  if (isDemoRegistrationSite()) {
    dbgI('PublicHome', 'demo registration site — routing to DemoAdmin or DemoLanding');
    if (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/')) {
      return <DemoAdmin />;
    }
    return <DemoLanding />;
  }
  if (bootPhase === 'checking') return <BootCheckingScreen />;
  // Unauthenticated: subdomains go to /login, root domain goes to /welcome (marketing landing).
  if (!authenticated) {
    if (subdomain) {
      dbgI('PublicHome', 'not authenticated — redirecting to /login');
      return <Navigate to="/login" replace />;
    }
    // /welcome is a static HTML file served by nginx, not a React route.
    // Use a full-page navigation so the browser exits the SPA entirely;
    // a React Router <Navigate> would hit the catch-all and loop back to /.
    dbgI('PublicHome', 'not authenticated on root domain — hard redirect to /welcome');
    window.location.replace('/welcome');
    return null;
  }
  // Authenticated on root domain but no tenant slug yet — wait for profile fetch.
  // Rendering Home before X-Tenant is available causes a 401 → /login loop.
  if (!subdomain && !tenantSlug) {
    if (waitTimedOut) {
      dbgW('PublicHome', 'tenant slug not resolved after 8s — showing recovery UI');
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg, #0d1b2a)', color: 'var(--text, #f1faee)' }}>
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <div style={{ fontSize: 14, marginBottom: 12, opacity: 0.7 }}>Couldn&rsquo;t load your department</div>
            <button
              onClick={() => { clearAuth(); window.location.replace('/login'); }}
              style={{ padding: '10px 24px', background: 'var(--accent, #e63946)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
            >Sign in again</button>
          </div>
        </div>
      );
    }
    dbgI('PublicHome', 'authenticated but tenant slug not yet resolved — waiting for profile fetch');
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg, #0d1b2a)',
      }}>
        <Wordmark variant="split" size={30} />
      </div>
    );
  }
  const isDemo = isDemoTenant();
  return (
    <>
      {isDemo && <DemoBanner />}
      <div style={isDemo ? { paddingTop: 'var(--demo-banner-h, 40px)' } : undefined}>
        <Navbar />
        <TenantRequired><Home /></TenantRequired>
        <AppFooter />
      </div>
    </>
  );
}

export default function App() {
  const [isLocked, setIsLocked] = useState(false);
  const [tenantKey, setTenantKey] = useState<string>(() => getActiveTenantSlug() || 'no-tenant');
  const hiddenAtRef = useRef<number | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLockMethod = hasPinSet() || hasBiometricRegistered();

  // Automatic mitigation for Chrome's "Desktop site" mode forcing this PWA into
  // desktop layout on a phone (see useForcedMobileLayout.ts for the full writeup
  // and the FORCED_MOBILE_LAYOUT_ENABLED kill switch). DesktopSiteBanner below
  // stays as the manual fallback regardless of whether this auto-mitigation is on.
  useForcedMobileLayout();

  // Listen for tenant context changes — bumping tenantKey forces the
  // entire <Routes> subtree to remount, so every page refetches tenant-scoped data.
  useEffect(() => {
    function handleTenantChanged(e: Event) {
      const detail = (e as CustomEvent<{ slug: string }>).detail;
      const next = detail?.slug || getActiveTenantSlug() || 'no-tenant';
      rkLog('tenantChanged: remounting routes for slug=', next);
      setTenantKey(next);
    }
    window.addEventListener('tenantChanged', handleTenantChanged);
    return () => window.removeEventListener('tenantChanged', handleTenantChanged);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (!isAuthenticated() || !hasLockMethod) return;
    idleTimerRef.current = setTimeout(() => setIsLocked(true), LOCK_TIMEOUT_MS);
  }, [hasLockMethod]);

  // Visibility change: lock after 15 min hidden
  useEffect(() => {
    function handleVisibility() {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        if (hiddenAtRef.current && isAuthenticated() && hasLockMethod) {
          if (Date.now() - hiddenAtRef.current > LOCK_TIMEOUT_MS) {
            setIsLocked(true);
          }
        }
        hiddenAtRef.current = null;
        resetIdleTimer();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [hasLockMethod, resetIdleTimer]);

  // Idle timer: lock after 15 min of no interaction
  useEffect(() => {
    if (!hasLockMethod) return;
    const events = ['click', 'keydown', 'touchstart', 'scroll'] as const;
    const handler = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [hasLockMethod, resetIdleTimer]);

  // Refresh stored user from live DB on every app load so role/credential
  // changes take effect without requiring logout.
  useEffect(() => {
    // On the OIDC callback route the session is still being established by
    // AuthCallback. Any auth state here is stale leftover from a previous
    // session — fetching the profile would 401 (noisy console error) and is
    // pointless: AuthCallback writes a fresh token then navigates away.
    if (isOnAuthCallback()) {
      dbgV('App', 'profile fetch skipped — on /auth/callback (session establishing)');
      return;
    }
    const auth = getAuth();
    if (!auth) {
      dbgV('App', 'profile fetch skipped — no auth state');
      return;
    }
    dbgI('App', 'fetching /api/auth/profile on mount', { email: auth.user.email });
    apiFetch<{
      role: string;
      credential_level: string;
      can_drive?: boolean;
      has_passkey?: boolean;
      timezone?: string;
      tenant_slug?: string;
      state?: string;
      county?: string;
      schedule_time_format?: 'short' | 'hhmm';
    } | null>('/api/auth/profile', { skipLogoutOn401: true })
      .then(data => {
        if (data) {
          dbgV('App', 'profile data received — updating stored user', {
            role: data.role,
            credential_level: data.credential_level,
            timezone: data.timezone,
            tenant_slug: data.tenant_slug,
          });
          saveAuth(auth.token, { ...auth.user, role: data.role, credential_level: data.credential_level, can_drive: data.can_drive ?? auth.user.can_drive, has_passkey: data.has_passkey, state: data.state ?? auth.user.state, county: data.county ?? auth.user.county, schedule_time_format: data.schedule_time_format ?? auth.user.schedule_time_format });
          // Store department timezone for time formatting
          const tz = data.timezone;
          if (tz) {
            import('./utils/time').then(({ setDeptTimezone }) => setDeptTimezone(tz));
          }
          // Keep active tenant slug fresh from profile — always update on root domain
          // so a hard-refresh never races between the async profile fetch and Home's
          // first API call (which needs X-Tenant to be set synchronously from localStorage).
          if (!isSubdomainLocked() && data.tenant_slug && !getActiveTenantSlug()) {
            dbgI('App', 'seeding active tenant from profile', { tenant_slug: data.tenant_slug });
            setActiveTenantSlug(data.tenant_slug);
          }
        } else {
          dbgW('App', '/api/auth/profile returned non-OK or empty — skipping user update');
        }
      })
      .catch(err => {
        dbgW('App', '/api/auth/profile fetch failed', { error: String(err) });
      });
  }, []);

  // Proactive token refresh: event-driven, not polled.
  // 5-minute access TTL makes polling pointless — by the time a 5-min interval
  // fires the token is either fine or already dead. Instead we refresh on:
  //   - mount (handle reopening a stale tab)
  //   - tab becoming visible again (backgrounded > TTL)
  //   - network coming back online (offline > TTL)
  // The apiFetch proactive-refresh guard handles the steady-state case on
  // every outgoing request. refreshSession() is a shared singleton so these
  // triggers never issue overlapping refresh POSTs.
  useEffect(() => {
    const check = async () => {
      // Don't touch tokens while AuthCallback is exchanging the code — any
      // state here is stale leftover and a refresh would 401 + clearAuth,
      // racing the in-flight exchange.
      if (isOnAuthCallback()) return;
      // Only attempt a refresh if we actually hold a refresh token. Without
      // this guard a stale access token with no/dead refresh token still
      // entered refreshSession() and produced a doomed POST /api/auth/refresh.
      if (isAuthenticated() && tokenExpiresSoon() && getRefreshToken()) {
        dbgI('App', 'token expiring soon — triggering proactive refresh');
        const ok = await refreshSession();
        dbgI('App', 'proactive refresh result', { success: ok });
      }
    };
    check();
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
    };
  }, []);



  return (
    <BrowserRouter>
      <UrlChangeLogger />
      <DesktopSiteBanner />
      <EnvBadge />
      {isLocked && isAuthenticated() && <LockScreen onUnlock={() => setIsLocked(false)} />}
      <InstallBanner />
      <PasskeyBanner />
      <DeviceBindingBanner />
      <ModuleProvider>
        <ErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
        <AuthBootGate>
        <Routes key={tenantKey}>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<PublicHome />} />
          <Route path="/calls/:id" element={<Protected><TenantRequired><ModuleGate module="calls"><CallDetail /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/calls/:id/:tab" element={<Protected><TenantRequired><ModuleGate module="calls"><CallDetail /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/history" element={<Protected><TenantRequired><ModuleGate module="calls"><CallHistory /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/location-directory" element={<Protected><TenantRequired><ModuleGate module="calls"><LocationAliasDirectory /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/timecard" element={<Protected><TenantRequired><ModuleGate module="scheduling"><Timecard /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/admin/patients" element={<Protected><TenantRequired><ModuleGate module="calls"><Patients /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/log/:callId?" element={<Protected><TenantRequired><EventLogRedirect /></TenantRequired></Protected>} />
          <Route path="/protocols" element={<Protected><TenantRequired><ModuleGate module="protocols"><Protocols /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/protocol-library" element={<Protected><TenantRequired><ModuleGate module="protocols"><PoolProtocols /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/protocol-index" element={<Protected><TenantRequired><ModuleGate module="protocols"><ProtocolIndex /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/admin/drive-protocol-sync" element={<Protected><TenantRequired><ModuleGate module="protocols"><DriveProtocolSyncAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/policies" element={<Protected><TenantRequired><ModuleGate module="protocols"><Policies /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/diversion" element={<Protected><TenantRequired><ModuleGate module="diversion"><Diversion /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/training" element={<Protected><TenantRequired><ModuleGate module="training"><Training /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/comms-trainer" element={<Protected><TenantRequired><ModuleGate module="comms_trainer"><CommsTrainer /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/members" element={<Protected><TenantRequired><ModuleGate module="members"><Members /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/messages" element={<Protected><TenantRequired><ModuleGate module="messaging"><Messages /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/messages/admin" element={<Protected><TenantRequired><ModuleGate module="messaging"><MessagingAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/scope-of-practice" element={<Protected><TenantRequired><ModuleGate module="protocols"><ScopeOfPractice /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/fire-danger" element={<Protected><TenantRequired><ModuleGate module="fire_danger"><FireDangerDetail /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
          <Route path="/admin/tenants" element={<Protected><TenantAdmin /></Protected>} />
          <Route path="/admin/modules" element={<Protected><AdminModules /></Protected>} />
          {/* #1524 — Dispatch Grants moved into the department-configuration
              (Tenant Admin) page as a super-admin-only tab; this keeps old
              bookmarks/links to the standalone page working. */}
          <Route path="/admin/dispatch-grants" element={<Protected><Navigate to="/admin/tenants" replace /></Protected>} />
          <Route path="/admin/agencies" element={<Protected><AdminAgencies /></Protected>} />
          <Route path="/admin/tone-monitor" element={<Protected><ToneMonitorAdmin /></Protected>} />
          <Route path="/admin/audit" element={<Protected><PlatformAuditLog /></Protected>} />
          <Route path="/admin/inbox" element={<Protected><AdminInbox /></Protected>} />
          <Route path="/reporting/neris" element={<Protected><TenantRequired><ModuleGate module="neris"><NerisAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/reporting/neris/:callId" element={<Protected><TenantRequired><ModuleGate module="neris"><NerisAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/reporting/neris/reconciliation" element={<Protected><TenantRequired><ModuleGate module="neris"><NerisReconciliation /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/admin/mabas" element={<Protected><ModuleGate module="mabas"><MabasAdmin /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/departments" element={<Protected><ModuleGate module="mabas"><MabasDepartments /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/cards" element={<Protected><ModuleGate module="mabas"><MabasCards /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/cards/new" element={<Protected><ModuleGate module="mabas"><MabasCardBuilder /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/cards/:cardId" element={<Protected><ModuleGate module="mabas"><MabasCardBuilder /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/div110" element={<Protected><ModuleGate module="mabas"><MabasDiv110 /></ModuleGate></Protected>} />
          <Route path="/admin/mabas/map" element={<Protected><ModuleGate module="mabas"><MabasMap /></ModuleGate></Protected>} />
          <Route path="/admin/dispatch-centers" element={<Protected><DispatchCenters /></Protected>} />
          <Route path="/admin/vehicles" element={<Protected><ModuleGate module="calls"><Vehicles /></ModuleGate></Protected>} />
          {/* No ModuleGate: map markers has no module-catalog entry — the page
              gates itself on the view_markers/manage_markers capabilities. */}
          <Route path="/admin/map-markers" element={<Protected><TenantRequired><MapMarkers /></TenantRequired></Protected>} />
          <Route path="/aeds" element={<Protected><TenantRequired><ModuleGate module="aed"><AedUnits /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/aed-inspections" element={<Protected><TenantRequired><ModuleGate module="aed"><AedInspections /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/aed-dashboard" element={<Protected><TenantRequired><ModuleGate module="aed"><AedDashboard /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/aed-program" element={<Protected><TenantRequired><ModuleGate module="aed"><AedProgramAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/inventory" element={<Protected><TenantRequired><ModuleGate module="inventory"><InventoryLocations /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/inventory/items" element={<Protected><TenantRequired><ModuleGate module="inventory"><InventoryItems /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/inventory/reorder-alerts" element={<Protected><TenantRequired><ModuleGate module="inventory"><ReorderAlerts /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/med-check" element={<Protected><TenantRequired><ModuleGate module="med_check"><MedCheckDashboard /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/med-check/inspections" element={<Protected><TenantRequired><ModuleGate module="med_check"><MedCheckInspections /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/admin/inspection-schedules" element={<Protected><TenantRequired><ModuleGate module="med_check"><InspectionSchedules /></ModuleGate></TenantRequired></Protected>} />
          {/* Phase 4 routes */}
          <Route path="/med-check/route" element={<Protected><TenantRequired><ModuleGate module="med_check"><MedCheckRoute /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/med-check/dhs110-export" element={<Protected><TenantRequired><ModuleGate module="med_check"><Dhs110Export /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/inventory/rig" element={<Protected><TenantRequired><ModuleGate module="inventory"><RigInventory /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/inventory/settings" element={<Protected><TenantRequired><ModuleGate module="inventory"><InventorySettings /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/admin/waitlist" element={<Protected><Waitlist /></Protected>} />
          <Route path="/schedule" element={<Protected><TenantRequired><ModuleGate module="scheduling"><Schedule /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/grid" element={<Protected><TenantRequired><ModuleGate module="scheduling"><ScheduleGrid /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/my" element={<Protected><TenantRequired><ModuleGate module="scheduling"><MySchedule /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/availability" element={<Protected><TenantRequired><ModuleGate module="scheduling"><AvailabilityEditor /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/shifts" element={<Navigate to="/schedule/settings" replace />} />
          <Route path="/schedule/timesheets" element={<Protected><TenantRequired><ModuleGate module="scheduling"><Timesheets /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/settings" element={<Protected><TenantRequired><ModuleGate module="scheduling"><SchedulingSettings /></ModuleGate></TenantRequired></Protected>} />
          {/* #1001 — Department Events moved out of Settings into its own tab. */}
          <Route path="/schedule/events" element={<Protected><TenantRequired><ModuleGate module="scheduling"><ScheduleEvents /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/schedule/log" element={<Protected><TenantRequired><ModuleGate module="scheduling"><AuditLog /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/disaster" element={<Protected><TenantRequired><ModuleGate module="disaster"><DisasterList /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/disaster/join" element={<DisasterGuestJoin />} />
          <Route path="/disaster/community/register" element={<DisasterCommunityRegister />} />
          <Route path="/disaster/community/report/:eventId" element={<DisasterCommunityReport />} />
          <Route path="/disaster/guest" element={<DisasterGuestSession />} />
          <Route path="/disaster/:eventId" element={<Protected><TenantRequired><ModuleGate module="disaster"><DisasterResponder /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/disaster/:eventId/command" element={<Protected><TenantRequired><ModuleGate module="disaster"><ErrorBoundary><DisasterCommand /></ErrorBoundary></ModuleGate></TenantRequired></Protected>} />
          <Route path="/disaster/:eventId/admin" element={<Protected><TenantRequired><ModuleGate module="disaster"><DisasterAdmin /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/disaster/:eventId/report" element={<Protected><TenantRequired><ModuleGate module="disaster"><DisasterReport /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/equipment-recovery" element={<Protected><TenantRequired><ModuleGate module="equipment_recovery"><EquipmentRecovery /></ModuleGate></TenantRequired></Protected>} />
          {/* Phase 5: vehicle tracking map */}
          <Route path="/tracking/map" element={<Protected><TenantRequired><ModuleGate module="equipment_tracking"><VehicleMap /></ModuleGate></TenantRequired></Protected>} />
          {/* Member & unit location tracking (plan-responsekit-2026-08-02) */}
          <Route path="/tracking/checkpoints" element={<Protected><TenantRequired><ModuleGate module="equipment_tracking"><LocationCheckpoints /></ModuleGate></TenantRequired></Protected>} />
          <Route path="/tracking/location-disclosure" element={<Protected><TenantRequired><LocationDisclosure /></TenantRequired></Protected>} />
          <Route path="/dashboard" element={<Protected><TenantRequired><ErrorBoundary><StationDashboard /></ErrorBoundary></TenantRequired></Protected>} />
          <Route path="/demo/register" element={<DemoRegister />} />
          <Route path="/demo/verify" element={<DemoVerify />} />
          <Route path="/demo/setup" element={<DemoSetPassword />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </AuthBootGate>
        </Suspense>
        </ErrorBoundary>
      </ModuleProvider>
    </BrowserRouter>
  );
}
